import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { DropdownOption } from '../../dropdown/types';
import { DropdownSize } from '../../dropdown/DropdownSize';
import { Menu } from '../../dropdown/Menu';
import { SimpleInputChip } from './SimpleInputChip';
import { withCssSelector } from '../../shared/withCssSelector';
import { Loader } from '../../loader';
import { useDropdownMenu } from '../../dropdown/useDropdownMenu';
import { Input } from '../input';
import {
	StyledSelectInput,
	StyledChevron,
	StyledSelectionItems,
	StyledPlaceholder,
	MenuSearchHeader,
} from './MultiSelectDropdown.style';

type MultiSelectDropdownProps = {
	className?: string;
	options: string[] | DropdownOption<string>[];
	selectedValues: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	loadingPlaceholder?: string;
	noMatchesPlaceholder?: string;
	noOptionsPlaceholder?: string;
	disabled?: boolean;
	searchable?: boolean;
	canRemove?: boolean;
	size?: DropdownSize;
	hasError?: boolean;
	loading?: boolean;
};

export const MultiSelectDropdown = withCssSelector(
	React.forwardRef<HTMLDivElement, MultiSelectDropdownProps>(function MultiSelectDropdown({
		className,
		options,
		selectedValues,
		onChange,
		placeholder,
		searchPlaceholder,
		loadingPlaceholder,
		noMatchesPlaceholder = '',
		noOptionsPlaceholder = '',
		disabled,
		searchable = false,
		canRemove = true,
		size = 'compact',
		hasError = false,
		loading = false,
	}, ref) {
		const [searchQuery, setSearchQuery] = useState('');
		const hasSelection = selectedValues.length > 0;
		const displayPlaceholder = loading ? loadingPlaceholder : placeholder;

		const { isOpen, onClose, onOpen, triggerRef, menuProps, renderMenu } =
			useDropdownMenu<HTMLDivElement>({
				sticky: true,
				triggerRef: ref,
				onCollapsed: () => setSearchQuery(''),
			});

		// Normalize options to DropdownOption format
		const normalizedOptions: DropdownOption<string>[] = useMemo(() => 
			options.map(option => {
				if (typeof option === 'string') {
					return { value: option, label: option };
				}
				return option;
			}),
		[options]);

		// Filter out already selected options and apply search
		const availableOptions = useMemo(() => {
			let filtered = normalizedOptions.filter(
				option => !selectedValues.includes(option.value)
			);

			if (searchable && searchQuery) {
				filtered = filtered.filter(option =>
					option.label.toLowerCase().includes(searchQuery.toLowerCase())
				);
			}

			return filtered;
		}, [normalizedOptions, selectedValues, searchable, searchQuery]);

		// Auto-close menu when no options are available (unless searchable)
		useEffect(() => {
			if (isOpen && availableOptions.length === 0 && !searchable) {
				onClose();
			}
		}, [availableOptions.length, isOpen, searchable, onClose]);

		// Reposition menu when selected values change (button height may change)
		useEffect(() => {
			if (isOpen && window.requestAnimationFrame) {
				// Use requestAnimationFrame to ensure DOM has updated
				window.requestAnimationFrame(() => {
					// Trigger Popper update by dispatching a resize event
					window.dispatchEvent(new Event('resize'));
				});
			}
		}, [selectedValues, isOpen]);

		const handleRemove = useCallback((value: string) => {
			const newValues = selectedValues.filter(v => v !== value);
			onChange(newValues);
		}, [selectedValues, onChange]);

		const handleSelect = useCallback((value: string) => {
			if (!selectedValues.includes(value)) {
				const newValues = [...selectedValues, value];
				onChange(newValues);
				setSearchQuery('');
			}
		}, [selectedValues, onChange]);

		const handleInputClick = useCallback(() => {
			if (!disabled && !loading) {
				if (isOpen) {
					onClose();
				} else {
					// Only open if there are available options or search is enabled
					if (availableOptions.length > 0 || searchable) {
						onOpen();
					}
				}
			}
		}, [disabled, loading, isOpen, onClose, onOpen, availableOptions.length, searchable]);

		return (
			<>
				<StyledSelectInput
					onClick={handleInputClick}
					$disabled={disabled}
					$engaged={isOpen && !searchable}
					$open={isOpen}
					$size={size}
					$hasError={hasError}
					className={className}
					ref={triggerRef}
					// Reachable and operable from the keyboard, like the input it draws as.
					tabIndex={disabled ? -1 : 0}
				>
					{!hasSelection ?
						(displayPlaceholder ?
							<StyledPlaceholder>
								{displayPlaceholder}
							</StyledPlaceholder>
							: null) :
						<StyledSelectionItems>
							{selectedValues.map(value => {
								const option = normalizedOptions.find(opt => opt.value === value);
								return (
									<SimpleInputChip
										key={value}
										value={value}
										label={option?.label}
										icon={option?.icon}
										onRemove={handleRemove}
										canRemove={canRemove && !disabled}
										size={size}
									/>
								);
							})}
						</StyledSelectionItems>}
					{loading ? <Loader /> : <StyledChevron 
						icon={'dropdown-chevron'} 
						onClick={isOpen ? onClose : undefined} 
					/>}
				</StyledSelectInput>
				{renderMenu(
					<Menu
						{...menuProps}
						options={availableOptions}
						onSelect={handleSelect}
						size={size}
						noOptionsMessage={searchQuery ? noMatchesPlaceholder : noOptionsPlaceholder}
						header={searchable && (
							<MenuSearchHeader>
								<Input
									size="compact"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder={searchPlaceholder ?? 'Search'}
									autoFocus
								/>
							</MenuSearchHeader>
						)}
					/>
				)}
			</>
		);
	})
	, StyledSelectInput.toString());
